import {
  useCompleteGoogleSignup,
  useCreateUser,
  type UserCreationPayload,
} from '@/api';
import { Button, Input, PhoneInput } from '@/components/atoms';
import type { OutletContext } from '@/components/layouts';
import { FormField } from '@/components/molecules';
import {
  clearGoogleSetupToken,
  getGoogleSetupToken,
} from '@/lib/auth/google-setup-token';
import { normalizeEmail } from '@/lib/helpers/normalize-email';
import { userCreationSchema } from '@/validation-schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useOutletContext, useSearchParams } from 'react-router';

interface PersonalProfileFormProps {
  setIdentifier: Dispatch<SetStateAction<string>>;
  setUserId?: Dispatch<SetStateAction<number | undefined>>;
}

export const PersonalProfileForm = ({
  setIdentifier,
  setUserId,
}: PersonalProfileFormProps) => {
  const { goToStep, credentials, accessToken, origin } =
    useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(userCreationSchema),
    mode: 'onChange',
  });

  const { mutate: createUser, isPending: isCreateUserPending } =
    useCreateUser();

  const { mutate: completeGoogleSignup, isPending: isGooglePending } =
    useCompleteGoogleSignup();

  useEffect(() => {
    if (credentials && origin === 'google') {
      setValue(
        'email',
        credentials.email ? normalizeEmail(credentials.email) : ''
      );
      setValue('firstName', credentials.given_name || '');
      setValue('lastName', credentials.family_name || '');
    }
    setValue('isGoogleSignup', origin === 'google');
  }, [credentials, setValue, origin]);

  useEffect(() => {
    const referrerCode =
      searchParams.get('ref') || searchParams.get('referrerCode');
    if (referrerCode?.trim()) {
      setValue('referrerCode', referrerCode.trim());
    }
  }, [searchParams, setValue]);

  const create = (userData: UserCreationPayload) => {
    const phone = userData.whatsappNumber;
    setIdentifier(phone);

    if (origin === 'google') {
      const setupToken = accessToken || getGoogleSetupToken();
      if (!setupToken) {
        toast.error('Your Google setup session expired. Please sign in again.');
        return;
      }

      completeGoogleSignup(
        {
          setupToken,
          firstName: userData.firstName,
          lastName: userData.lastName,
          whatsappNumber: phone,
          ...(userData.referrerCode?.trim()
            ? { referrerCode: userData.referrerCode.trim() }
            : {}),
        },
        {
          onSuccess: (response) => {
            clearGoogleSetupToken();
            const data = response.data;
            const whatsapp = data.whatsappNumber || phone;
            setUserId?.(data.id);
            setIdentifier(whatsapp);
            // Always verify WhatsApp before business profile on Google signup.
            goToStep(2, { userId: data.id, identifier: whatsapp });
          },
        }
      );
      return;
    }

    createUser(userData, {
      onSuccess: (response) => {
        const created = response.data;
        setUserId?.(created.userId);
        goToStep(2, { userId: created.userId, identifier: phone });
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(create)}
      className="grid grid-cols-2 gap-4 px-4 md:px-6 lg:px-8"
    >
      <FormField
        label="First name"
        className="max-md:col-span-2"
        error={errors.firstName?.message}
      >
        <Input
          placeholder="eg. Ishaya"
          {...register('firstName')}
          disabled={!!credentials?.given_name && origin === 'google'}
        />
      </FormField>
      <FormField
        label="Last name"
        className="max-md:col-span-2"
        error={errors.lastName?.message}
      >
        <Input
          placeholder="eg. Solomon"
          {...register('lastName')}
          disabled={!!credentials?.family_name && origin === 'google'}
        />
      </FormField>
      <FormField
        label="Email"
        className="col-span-2"
        error={errors.email?.message}
      >
        <Input
          placeholder="example@gosellyt.com"
          type="email"
          {...register('email')}
          disabled={!!credentials?.email && origin === 'google'}
        />
      </FormField>
      <FormField
        label="Whatsapp number"
        className="col-span-2"
        error={errors.whatsappNumber?.message}
        infoTip="Enter the phone number connected to your WhatsApp Business account (not a personal WhatsApp number)."
      >
        <Controller
          name="whatsappNumber"
          control={control}
          render={({ field }) => (
            <PhoneInput
              value={field.value}
              onChange={(value) => {
                field.onChange(`+${value}`);
              }}
              country="ng"
              placeholder="+234 801 234 5678"
              defaultMask="... ... ...."
              onBlur={field.onBlur}
            />
          )}
        />
      </FormField>
      <FormField
        label="Referral code (optional)"
        className="col-span-2"
        error={errors.referrerCode?.message}
      >
        <Input placeholder="eg. DAUKKKMZ" {...register('referrerCode')} />
      </FormField>
      {origin !== 'google' && (
        <FormField
          label="Password"
          className="col-span-2"
          error={errors.password?.message}
        >
          <Input
            type="password"
            placeholder="minimum 8 characters"
            {...register('password')}
          />
        </FormField>
      )}
      <Button
        className="col-span-2"
        label="Create profile"
        size="lg"
        isLoading={isCreateUserPending || isGooglePending}
        disabled={!isValid}
      />
    </form>
  );
};
